<template>
  <div>
    <loading-wrapper :is-loading="!checkSchemas.length || !config">
      <div v-if="config && checkSchemas.length">
        <check-bot-configurator-header
          v-model="config"
          class="configurator-header"
          :is-creating-new-config="isCreatingNewConfig"
          :supplier-id="supplierId"
        />
        <check-configuration
          v-for="(check, index) in config?.checks"
          :key="check.name"
          v-model="config.checks[index]"
          :check-schema="getCheckSchema(check.name)"
          @delete="deleteCheck"
        />
        <add-check-select
          :added-checks="config?.checks"
          :check-schemas="checkSchemas"
          class="add-check-select"
          @check-selected="addCheck"
        />
        <div class="buttons-container">
          <vf-button
            :disabled="isSavingConfig"
            :size="buttonSizes.medium"
            :variant="buttonVariants.primary"
            @click="exitConfigurator"
            >{{ t('generic.cancel') }}</vf-button
          >
          <vf-button
            :busy="isSavingConfig"
            :disabled="!isConfigValid"
            :size="buttonSizes.medium"
            :variant="buttonVariants.secondary"
            @click="saveConfig"
            >{{ saveButtonLabel }}</vf-button
          >
        </div>
      </div>
    </loading-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useRouter } from 'vue-router';
  import { CheckBotConfigsService } from '@/services';
  import {
    CheckConfiguration as CheckConfigurationSchema,
    ICreateCheckBotConfigurationDto,
  } from 'types';
  import CheckBotConfiguratorHeader from '@/components/CheckBotConfiguratorHeader/CheckBotConfiguratorHeader.vue';
  import CheckConfiguration from '@/components/CheckConfiguration/CheckConfiguration.vue';
  import AddCheckSelect from '@/components/AddCheckSelect/AddCheckSelect.vue';
  import { HttpStatusCode, isAxiosError } from 'axios';
  import { Route, routeParams } from '@/routes';
  import { ProjectsSupplierDetailViewTab } from '../ProjectsSupplierDetailView/ProjectsSupplierDetailView.types';
  import { DigitalizationProjectDetailsTab } from '../DigitalizationProjectDetails/DigitalizationProjectDetails.types';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const router = useRouter();

  const props = defineProps<{
    configId?: string;
    projectId: string;
    supplierId?: string;
    templateId?: string;
  }>();

  const checkSchemas = ref<CheckConfigurationSchema[]>([]);
  const config = ref<ICreateCheckBotConfigurationDto | null>(null);
  const isFetchingCheckSchemas = ref(false);
  const isFetchingConfig = ref(false);
  const isSavingConfig = ref(false);

  const isCreatingNewConfig = computed(() => !props.configId);
  const isStartingEmpty = computed(() => !(props.configId || props.templateId));
  const isConfigValid = computed(() => {
    if (!config.value || !checkSchemas.value.length) return false;
    return CheckBotConfigsService.validateCheckBotConfiguration(
      config.value,
      checkSchemas.value,
    );
  });
  const saveButtonLabel = computed(() => {
    return props.configId
      ? t('checkBotConfigurationView.saveConfiguration')
      : t('checkBotConfigurationView.createConfiguration');
  });

  async function getConfig(): Promise<void> {
    if (!isStartingEmpty.value) fetchConfig();
    else {
      config.value = {
        name: t('checkBotConfigurationView.configName'),
        description: t('checkBotConfigurationView.configDescription'),
        checks: [],
      };
    }
  }

  async function fetchConfig(): Promise<void> {
    isFetchingConfig.value = true;
    try {
      config.value = await CheckBotConfigsService.getConfigOrTemplate(
        props.configId,
        props.templateId,
      );
    } catch (err) {
      createErrorToast(
        t('checkBotConfigurationView.errors.errorGettingCheckBotConfig'),
      );
    } finally {
      isFetchingConfig.value = false;
    }
  }

  async function fetchCheckSchemas(): Promise<void> {
    isFetchingCheckSchemas.value = true;
    try {
      checkSchemas.value = await CheckBotConfigsService.getCheckSchemas();
    } catch {
      createErrorToast(
        t('checkBotConfigurationView.errors.errorGettingCheckSchemas'),
      );
    } finally {
      isFetchingCheckSchemas.value = false;
    }
  }

  async function saveConfig(): Promise<void> {
    if (!config.value) return;
    isSavingConfig.value = true;
    try {
      if (props.configId) {
        await CheckBotConfigsService.updateConfig(props.configId, config.value);
      } else {
        await CheckBotConfigsService.createConfig(
          config.value,
          props.projectId,
          props.supplierId,
        );
      }
      createSuccessToast(t('checkBotConfigurationView.configSaved'));
      exitConfigurator();
    } catch (err) {
      if (
        isAxiosError(err) &&
        err.response?.status === HttpStatusCode.Conflict
      ) {
        createErrorToast(
          t('checkBotConfigurationView.errors.duplicateNameError'),
        );
      } else {
        createErrorToast(
          t('checkBotConfigurationView.errors.errorSavingConfig'),
        );
      }
    } finally {
      isSavingConfig.value = false;
    }
  }

  function getCheckSchema(checkName: string): CheckConfigurationSchema {
    const schema = checkSchemas.value?.find(
      (check) => check.name === checkName,
    );
    if (!schema) {
      createErrorToast();
      throw new Error(`Check schema not found for name: ${checkName}`);
    }
    return schema;
  }

  function addCheck(checkSchema: CheckConfigurationSchema): void {
    if (!config.value) return;
    config.value.checks.push({
      name: checkSchema.name,
      params: [],
      results: [],
    });
  }

  function deleteCheck(checkName: string): void {
    if (!config.value) return;
    config.value.checks = config.value.checks.filter(
      (check) => check.name !== checkName,
    );
  }

  function exitConfigurator(): void {
    if (props.supplierId) {
      router.push({
        name: Route.PROJECT_SUPPLIER_DETAILS,
        params: {
          [routeParams[Route.PROJECT_SUPPLIER_DETAILS].projectId]:
            props.projectId,
          [routeParams[Route.PROJECT_SUPPLIER_DETAILS].supplierId]:
            props.supplierId,
          [routeParams[Route.PROJECT_SUPPLIER_DETAILS].tab]:
            ProjectsSupplierDetailViewTab.CHECK_BOT,
        },
      });
    } else {
      router.push({
        name: Route.DIGITALIZATION_PROJECT_DETAILS,
        params: {
          [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]:
            props.projectId,
          [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].tab]:
            DigitalizationProjectDetailsTab.SETTINGS,
        },
      });
    }
  }

  fetchCheckSchemas();
  getConfig();
</script>

<style lang="scss" scoped>
  .configurator-header {
    margin-bottom: 2rem;
  }
  .add-check-select {
    width: 60%;
  }
  .buttons-container {
    margin-top: 3rem;
    display: flex;
    justify-content: space-between;
  }
</style>
